/**
 * AdminDashboard
 * Shows system stats and recent import jobs for admins.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, UploadCloud, Users, Activity, Zap } from 'lucide-react';
import { useLingui } from '@lingui/react';
import { useApiClient } from '../api/client';
import type { AdminStatsResponse, AdminJobsResponse, FeatureFlag } from '../api/client';
import { parseDbDate } from '../lib/date';

export default function AdminDashboard() {
  const { _ } = useLingui();
  const api = useApiClient();
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [jobs, setJobs] = useState<AdminJobsResponse | null>(null);
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [toggling, setToggling] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void Promise.all([api.adminGetStats(), api.adminGetJobs(10), api.adminGetFeatureFlags()])
      .then(([s, j, f]) => {
        setStats(s);
        setJobs(j);
        setFlags(f);
      })
      .finally(() => setLoading(false));
  }, [api]);

  async function handleToggleFlag(key: string, currentEnabled: boolean) {
    setToggling(key);
    try {
      const updated = await api.adminUpdateFeatureFlag(key, !currentEnabled);
      setFlags((prev) => prev.map((f) => (f.key === key ? updated : f)));
    } finally {
      setToggling(null);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="overflow-x-auto rounded-2xl shadow border border-base-300">
        <div className="stats stats-vertical sm:stats-horizontal w-full bg-base-100 min-w-120">
          <div className="stat">
            <div className="stat-figure text-primary hidden sm:flex">
              <UploadCloud size={32} strokeWidth={2.25} aria-hidden="true" />
            </div>
            <div className="stat-title">{_({ id: 'admin.dashboard.stats.totalImports' })}</div>
            <div className="stat-value">{stats?.totalJobs ?? 0}</div>
          </div>
          <div className="stat">
            <div className="stat-figure text-info hidden sm:flex">
              <Users size={32} strokeWidth={2.25} aria-hidden="true" />
            </div>
            <div className="stat-title">{_({ id: 'admin.dashboard.stats.totalUsers' })}</div>
            <div className="stat-value">{stats?.totalUsers ?? 0}</div>
          </div>
          <div className="stat">
            <div className="stat-figure text-accent hidden sm:flex">
              <Activity size={32} strokeWidth={2.25} aria-hidden="true" />
            </div>
            <div className="stat-title">{_({ id: 'admin.dashboard.stats.byStatus' })}</div>
            <div className="stat-value">{stats?.byStatus.length ?? 0}</div>
            <div className="stat-desc flex flex-wrap gap-1 mt-1">
              {stats?.byStatus.map((s) => (
                <span key={s.status} className="badge badge-sm badge-outline">
                  {s.status}: {s.count}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Links */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-base">
              {_({ id: 'admin.dashboard.management.title' })}
            </h2>
            <div className="flex flex-col gap-2">
              <Link to="/admin/exchanges" className="btn btn-outline btn-sm justify-start gap-2">
                <Building2 size={16} strokeWidth={2.25} aria-hidden="true" />
                {_({ id: 'admin.dashboard.management.catalogLink' })}
              </Link>
            </div>
          </div>
        </div>

        {/* By Source */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-base">{_({ id: 'admin.dashboard.bySource.title' })}</h2>
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>{_({ id: 'admin.dashboard.bySource.colSource' })}</th>
                    <th>{_({ id: 'admin.dashboard.bySource.colCount' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {!stats?.bySource.length && (
                    <tr>
                      <td colSpan={2} className="text-center text-base-content/40 py-6">
                        {_({ id: 'admin.dashboard.bySource.empty' })}
                      </td>
                    </tr>
                  )}
                  {stats?.bySource.map((s) => (
                    <tr key={s.source}>
                      <td>
                        <span className="badge badge-ghost badge-sm font-mono">{s.source}</span>
                      </td>
                      <td className="tabular-nums">{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Recent import jobs */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <div className="flex items-center justify-between">
            <h2 className="card-title text-base">{_({ id: 'admin.dashboard.jobs.title' })}</h2>
            <span className="text-xs text-base-content/50">
              {_({ id: 'admin.dashboard.jobs.last10' })}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="table table-sm table-zebra min-w-140">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{_({ id: 'admin.dashboard.jobs.colUser' })}</th>
                  <th>{_({ id: 'admin.dashboard.jobs.colYear' })}</th>
                  <th>{_({ id: 'admin.dashboard.jobs.colSource' })}</th>
                  <th>{_({ id: 'admin.dashboard.jobs.colStatus' })}</th>
                  <th>{_({ id: 'admin.dashboard.jobs.colDate' })}</th>
                </tr>
              </thead>
              <tbody>
                {!jobs?.jobs.length && (
                  <tr>
                    <td colSpan={6} className="text-center text-base-content/40 py-6">
                      {_({ id: 'admin.dashboard.jobs.empty' })}
                    </td>
                  </tr>
                )}
                {jobs?.jobs.map((job) => (
                  <tr key={job.id}>
                    <td className="font-mono text-xs">{job.id.slice(-8)}</td>
                    <td className="text-xs">{job.userId.slice(-12)}</td>
                    <td>{job.taxYear}</td>
                    <td>
                      <span className="badge badge-sm badge-ghost font-mono">{job.source}</span>
                    </td>
                    <td>
                      <span
                        className={`badge badge-sm ${
                          job.status === 'done'
                            ? 'badge-success'
                            : job.status === 'error'
                              ? 'badge-error'
                              : job.status === 'processing'
                                ? 'badge-warning'
                                : 'badge-neutral'
                        }`}
                      >
                        {job.status}
                      </span>
                    </td>
                    <td className="text-xs">
                      {parseDbDate(job.createdAt).toLocaleDateString('tr-TR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Feature Flags */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={18} strokeWidth={2.25} className="text-warning" aria-hidden="true" />
            <h2 className="card-title text-base">Feature Flags</h2>
          </div>
          {flags.length === 0 ? (
            <p className="text-sm text-base-content/40 py-4 text-center">No feature flags found.</p>
          ) : (
            <div className="flex flex-col divide-y divide-base-200">
              {flags.map((flag) => (
                <div key={flag.key} className="flex items-center justify-between py-3 gap-4">
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="font-mono text-sm font-medium">{flag.key}</span>
                    {flag.description && (
                      <span className="text-xs text-base-content/50 truncate">
                        {flag.description}
                      </span>
                    )}
                    {flag.updatedBy && (
                      <span className="text-xs text-base-content/40">
                        Updated by: {flag.updatedBy}
                      </span>
                    )}
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer shrink-0">
                    {toggling === flag.key && (
                      <span className="loading loading-spinner loading-xs" />
                    )}
                    <input
                      type="checkbox"
                      className="toggle toggle-success toggle-sm"
                      checked={flag.isEnabled}
                      disabled={toggling === flag.key}
                      onChange={() => void handleToggleFlag(flag.key, flag.isEnabled)}
                    />
                    <span
                      className={`text-xs font-medium ${flag.isEnabled ? 'text-success' : 'text-base-content/40'}`}
                    >
                      {flag.isEnabled ? 'ON' : 'OFF'}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
